import { useEffect, useRef, type ReactNode } from 'react'
import { Emblem, type EmblemName } from './brand/Emblems'
import { CloseButton } from './CloseButton'

type Props = {
  open: boolean
  title: ReactNode
  emblem?: EmblemName
  onClose: () => void
  children: ReactNode
  /** Accessible name when the title is not plain text. */
  label?: string
}

// `a[href]`, not `[href]`: emblem sprites are <svg><use href> and must not count.
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const focusables = (node: HTMLElement) =>
  Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(el => !el.closest('svg') && el.getClientRects().length > 0)

/** Bottom sheet for phones: dims the window, traps focus, closes on Escape
 *  and returns focus to the element that opened it. */
export function Sheet({ open, title, emblem, onClose, children, label }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const opener = useRef<HTMLElement | null>(null)
  // Callers pass inline handlers; keep the effect keyed on `open` only so a
  // parent re-render never bounces focus back to the opener.
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    if (!open) return
    opener.current = document.activeElement as HTMLElement | null
    const node = ref.current
    if (node) (focusables(node)[0] ?? node).focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        close.current()
        return
      }
      if (e.key !== 'Tab' || !node) return
      const items = focusables(node)
      if (items.length === 0) {
        e.preventDefault()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const inside = node.contains(document.activeElement)
      if (!inside || (e.shiftKey && document.activeElement === first)) {
        e.preventDefault()
        ;(e.shiftKey ? last : first).focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      opener.current?.focus?.()
    }
  }, [open])
  if (!open) return null
  return (
    <>
      <div className="dim-under" onClick={onClose} aria-hidden="true" />
      <div className="sheet" ref={ref} role="dialog" tabIndex={-1} aria-modal="true" aria-label={label ?? (typeof title === 'string' ? title : undefined)}>
        <div className="sheet__grip" aria-hidden="true" />
        <div className="sheet__head">
          {emblem && <Emblem name={emblem} />}
          <span>{title}</span>
          <CloseButton className="x" onClose={onClose} />
        </div>
        {children}
      </div>
    </>
  )
}
